<script setup lang="ts">
import { computed } from 'vue'
import Button from 'primevue/button'

export type ButtonColor = 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'info'
export type ButtonVariant = 'solid' | 'outline' | 'text' | 'link'
export type ButtonSize = 'small' | 'medium' | 'large'

interface Props {
  label?: string
  /** HTML `<button type=>` attribute. */
  type?: 'button' | 'submit' | 'reset'
  /** Semantic color. */
  color?: ButtonColor
  /** Visual appearance — orthogonal to color. */
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  disabled?: boolean
  /** PrimeIcons name (without `pi-` prefix). */
  icon?: string
  iconPos?: 'left' | 'right'
  /** Full-width button (`width: 100%`). */
  block?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
  color: 'primary',
  variant: 'solid',
  size: 'medium',
  iconPos: 'left',
  loading: false,
  disabled: false,
  block: false,
})

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

// PrimeVue 4 uses `warn` for warning severity.
const primeSeverity = computed(() => (props.color === 'warning' ? 'warn' : props.color))

const isOutlined = computed(() => props.variant === 'outline')
const isText = computed(() => props.variant === 'text')
const isLink = computed(() => props.variant === 'link')
const normalizedIcon = computed(() => {
  if (!props.icon) return undefined
  if (props.icon.includes(' ')) return props.icon
  return props.icon.startsWith('pi-') ? `pi ${props.icon}` : `pi pi-${props.icon}`
})

const buttonClasses = computed(() => [
  'base-button',
  `base-button--${props.size}`,
  `base-button--color-${props.color}`,
  `base-button--variant-${props.variant}`,
  { 'base-button--block': props.block },
])
</script>

<template>
  <Button
    :label="label"
    :type="type"
    :severity="primeSeverity"
    :outlined="isOutlined"
    :text="isText"
    :link="isLink"
    :loading="loading"
    :disabled="disabled"
    :icon="normalizedIcon"
    :icon-pos="iconPos"
    :class="buttonClasses"
    @click="emit('click', $event)"
  >
    <slot v-if="$slots.default" />
  </Button>
</template>

<style scoped>
.base-button {
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.2s ease;
}

/* Full-width — replaces class="w-full" at call sites */
.base-button--block {
  width: 100%;
}

/* Primary solid — Option 2 diagonal indigo gradient + premium glow.
   The CTA reads as the single intentional spotlight on the page. */
.base-button--color-primary.base-button--variant-solid {
  /* background: linear-gradient(135deg, #6d5ef9 0%, #7c5cff 100%) !important; */
  background: linear-gradient(rgb(92, 60, 240) 0%, rgb(80, 46, 212) 100%);
  border-color: var(--p-primary-600) !important;
  color: var(--p-primary-contrast-color) !important;
  font-weight: 600 !important;
  box-shadow: 0 8px 24px rgba(109, 94, 249, 0.24);
}

.base-button--color-primary.base-button--variant-solid:not(:disabled):hover {
  background: linear-gradient(135deg, #5a49e6 0%, #6d5ef9 100%) !important;
  border-color: var(--p-primary-700) !important;
  box-shadow: 0 12px 32px rgba(109, 94, 249, 0.32);
}

.base-button--color-primary.base-button--variant-solid:not(:disabled):active {
  background: var(--p-primary-700) !important;
  border-color: var(--p-primary-800) !important;
  box-shadow: 0 2px 4px -1px rgba(124, 92, 255, 0.18) inset;
}

/* Disabled — unambiguously greyed out, never a faded brand color */
.base-button--variant-solid:disabled,
.base-button--variant-solid.p-disabled {
  background: var(--p-surface-200) !important;
  border-color: var(--p-surface-200) !important;
  color: var(--p-text-muted-color) !important;
  cursor: not-allowed;
  opacity: 1 !important;
}

.base-button--variant-outline:disabled,
.base-button--variant-outline.p-disabled {
  background: transparent !important;
  border-color: var(--p-surface-200) !important;
  color: var(--p-surface-400) !important;
  cursor: not-allowed;
  opacity: 0.5 !important;
  pointer-events: none;
}

/* Link variant — underlined inline text */
.base-button--variant-link {
  background: none !important;
  border: none !important;
  border-radius: 0 !important;
  text-decoration: underline !important;
  padding: 0 !important;
  box-shadow: none !important;
  font-weight: 600 !important;
}

.base-button--variant-link:not(:disabled):hover {
  background: none !important;
}

/* Text variant — no border, no underline */
.base-button--variant-text {
  background: none !important;
  border: none !important;
  text-decoration: none !important;
  padding: 0 !important;
  box-shadow: none !important;
  font-weight: 600 !important;
}

.base-button--variant-text:not(:disabled):hover {
  background: none !important;
}

/* Text variant color overrides */
.base-button--color-danger.base-button--variant-text {
  color: var(--p-danger-600) !important;
  background: var(--p-danger-50) !important;
}

.base-button--color-danger.base-button--variant-text:not(:disabled):hover {
  color: var(--p-danger-700) !important;
  background: var(--p-danger-100) !important;
}

/* Outline danger variant */
.base-button--color-danger.base-button--variant-outline {
  background: var(--p-danger-50) !important;
  border-color: var(--p-danger-300) !important;
  color: var(--p-danger-600) !important;
  font-weight: 600 !important;
}

.base-button--color-danger.base-button--variant-outline:not(:disabled):hover {
  background: var(--p-danger-100) !important;
  border-color: var(--p-danger-400) !important;
  color: var(--p-danger-700) !important;
}

/* Sizes */
.base-button--small {
  font-size: 0.8rem;
  /* padding: 0.4rem 0.8rem; */
  padding: 0.6rem !important;
}

.base-button--medium {
  font-size: 0.875rem;
  padding: 0.6rem 1rem;
}

.base-button--large {
  font-size: 1rem;
  padding: 0.75rem 1.25rem;
}
</style>
